import { html, View } from 'rune-ts';
import { BaseInputAttribute, BaseInputType } from '../types';
import {
  BaseInputAttributeChangedEvent,
  BaseInputBlurEvent,
  BaseInputFocusEvent,
  BaseInputInputEvent,
} from '../events';
import { isUndefined } from '@fxts/core';
import input_klass from '../../DateInputField/styles/BaseInputView.module.scss';

export abstract class AbstractBaseInputView<T = unknown> extends View<
  Readonly<{
    value?: T;
    attribute?: BaseInputAttribute;
    props?: {
      klass?: string;
    };
  }>
> {
  protected readonly type: BaseInputType;
  protected value?: T;
  protected klass?: string;
  protected attribute: BaseInputAttribute;

  protected constructor(
    type: BaseInputType,
    value?: T,
    attribute?: BaseInputAttribute,
    props?: {
      klass?: string;
    },
  ) {
    super({
      value,
      attribute,
      props,
    });
    this.type = type;
    this.value = value;
    this.attribute = attribute ?? {};
    this.klass = props?.klass;
  }

  protected override template() {
    const { name, disabled, readonly, placeholder = '' } = this.attribute;

    return html`
      <input
        type="${this.type}"
        name="${name}"
        value="${this.displayValue}"
        placeholder="${placeholder}"
        ${disabled ? 'disabled' : ''}
        ${readonly ? 'readonly' : ''}
        class="${this.klass || ''}"
      />
    `;
  }

  protected override onRender() {
    this.addEventListener('input', (e) => {
      if (e.target instanceof HTMLInputElement) {
        const value = this.parseValue(e.target.value);
        this.setValueAndSync(value);
        this.dispatchEvent(BaseInputInputEvent, {
          bubbles: true,
        });
      }
    });

    this.addEventListener('focus', () => {
      this.dispatchEvent(BaseInputFocusEvent, {
        bubbles: true,
      });
    });

    this.addEventListener('blur', () => {
      this.transformValue();
      this.setValueAndSync(this.value);

      this.dispatchEvent(BaseInputBlurEvent, {
        bubbles: true,
      });
    });
  }

  protected abstract parseValue(value: string | undefined): T | undefined;

  protected transformValue(): this {
    return this;
  }

  protected formatValue(value: T | undefined): string {
    return isUndefined(value) ? '' : String(value);
  }

  public sync(): this {
    const element = this.element();

    if (element instanceof HTMLInputElement) {
      element.value = this.displayValue;
    }
    return this;
  }

  public setValue(value: T | undefined): void {
    this.value = value;
  }

  public setValueAndSync(value: T | undefined): void {
    this.setValue(value);
    this.sync();
  }

  public getValue(): T | undefined {
    return this.value;
  }

  public getAttribute() {
    return this.attribute;
  }

  public setAttribute(attribute: Partial<BaseInputAttribute>) {
    this.attribute = {
      ...this.attribute,
      ...attribute,
    };

    this.dispatchEvent(BaseInputAttributeChangedEvent, {
      bubbles: true,
      detail: this.attribute,
    });
  }

  public get displayValue() {
    return this.formatValue(this.value);
  }

  public setError(error: boolean) {
    if (error) {
      this.element().classList.add(input_klass.input_error);
    } else {
      this.element().classList.remove(input_klass.input_error);
    }
  }
}
